import _ from 'lodash';
import moment from 'moment/moment';
import uuidv4 from 'uuid/v4';
import {
  COLUMN_GAP,
  DEFAULT_LEFT,
  OFFSET,
} from '../components/edReview/calendarView/dailyCalendar/edReviewCalendarDaily';
import {calculateDifferenceInMinutes} from '../../../../bedManagement/bmUtils';
import _isEmpty from 'lodash/isEmpty';
import {getSessionSlotDuration, isSessionActive} from '../components/edReview/utils/sessionUtilities';
import {KIND_OF_REVIEW_STATE} from '../../../../../store/reducers/reviews';

export const DAYS_OF_WEEK = [
  { id: "Mon", name: "Mon", value: "1" },
  { id: "Tue", name: "Tue", value: "2" },
  { id: "Wed", name: "Wed", value: "3" },
  { id: "Thu", name: "Thu", value: "4" },
  { id: "Fri", name: "Fri", value: "5" },
  { id: "Sat", name: "Sat", value: "6" },
  { id: "Sun", name: "Sun", value: "7" },
];

export const INIT_ERROR_STATE = {
  startTime: false,
  startDate: false,
  endTime: false,
  endDate: false,
  customTime: {},
  slotDurationInMinutes: false,
};

export const TIME_INTERVAL = [
  { id: "week", name: "Every Week", value: "week" },
  { id: "2week", name: "Every Two Weeks", value: "2week" },
  { id: "3week", name: "Every Three Weeks", value: "3week" },
  { id: "4week", name: "Every Four Weeks", value: "4week" },
];

export const CUSTOM_INTERVAL_OPTIONS = [
  { id: "day", name: "Day", value: "day" },
  { id: "week", name: "Week", value: "week" },
  { id: "month", name: "Month", value: "month" },
];

// export const ERROR_MESSAGES = {
//   interval: "Please provide either custom or default session interval.",
//   daysOfWeekExceedsTimespan: "Days of Week input exceeds timespan between start and end date. To proceed either set valid proper days of week or set one of available intervals."
// }

export const INITIAL_SESSION_STATE = {
  sessionId: uuidv4(),
  daysOfWeek: [], // arr of isoWeekday (string-typed integers)
  customSessionTime: {}, // { [dayOfWeek]: { startTime: null, endTime: null} }
  startTime: moment(),
  startDate: moment(),
  endTime: null,
  endDate: null,
  sessionName: "",
  fixedSlotDuration: false,
  slotDurationInMinutes: "",
  intervalModeOn: true,
  intervalType: null,
  customInterval: null,
  customIntervalNumber: null,
  customIntervalType: null,
  active: true,
  locked: false,
  mapId: "",
  clinicsId: "",
  lockedReason: "",
  omitDates: [],
  healthCareService: null,
  assignedSpecialty: null,
  practitioner: null,
  practitionerRole: null,
};

export const APPOINTMENT_SESSION_MODE = {
  BARS: "BaRS",
  ORDINARY: "ordinary",
};

export const ED_REVIEW_MODALS = {
  ASSIGN_SESSION: "assignSession",
  ADD_EDIT_SESSION: "addEditEdSession",
  CHANGE_REVIEW_STATUS: "changeReviewStatus",
};

export const ED_REVIEW_PATIENT_STATUSES = {
  STARTED: "started",
  FINISHED: "finished",
  IN_REVIEW: "inReview",
  CANCELLED: "cancelled",
  REGISTERED: "registered",
  NOT_ASSIGNED: "notAssigned",
  UNREGISTERED: "notRegistered",
};

// State used in day calendar context when locking, creating and modyfying created event slots
export const SLOT_MANIPULATION_OPTIONS = {
  LOCK_SLOT: "lock",
  CREATE_SLOT: "create",
  ASSIGN: "assign",
  RESCHEDULE_LOCKED: "rescheduleLocked",
  RESCHEDULE_PATIENT: "reschedulePatient",
};

const TIMER_COUNTDOWN_MINUTES = {
  yellow: 30,
  red: 5,
};

export const DRAG_TYPES = {
  LOCKED: "locked",
  PATIENT: "patient",
  TEMPORARY_LOCKED: "temporaryLocked",
};

export const DEFAULT_SLOT_DURATION_IN_MINUTES = 30;

////////////////////////////////////////////////
////             PT Data             ////
///////////////////////////////////////////////

export const getChiefComplaint = (pt, includeForm = false) => {
  let toReturn = "-";
  if (pt?.clerking?.chiefComplaint)
    toReturn = includeForm ? pt?.clerking : pt?.clerking?.chiefComplaint;
  if (pt?.triage?.chiefComplaint)
    toReturn = includeForm ? pt?.triage : pt?.triage?.chiefComplaint;
  if (pt?.admission?.initialAEComplaintDescription)
    toReturn = includeForm
      ? pt?.admission
      : pt?.admission?.initialAEComplaintDescription;

  return _.isObject(toReturn) && !includeForm
    ? toReturn?.__description
    : toReturn;
};

// export const getConsultantSpecialty = (consultant) => consultant?.specialties;

export const getReviewData = (pt, isED = true) => isED && pt?.bedas;

export const getSessionId = (session) => session?.sessionId || session?.id;

export const isSessionSingleEvent = (session) =>
  session?.singleEvent || !!session?.from || !!session?.to;

const isValidUUID = (value) => {
  const regex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return regex.test(value) && _.isString(value);
};

////////////////////////////////////////////////
////          TIME UTILS          ////
///////////////////////////////////////////////

export const cleanHour = (date, format) =>
  moment(date, format && format).set({ hours: 0, minutes: 0, seconds: 0 });

export const cleanDate = (date, format) =>
  moment().set({
    hours: moment(date, format && format).hours(),
    minutes: moment(date, format && format).minutes(),
  });

const comparedMinutesAreSame = (date1, date2) =>
  moment(cleanDate(date1)).isSame(cleanDate(date2), "minutes");

export const customSessionTimeDiffers = (sessionState, customDayHourObj) => {
  const { startTime: startCustomDate, endTime: endCustomDate } =
    customDayHourObj;
  const { startTime: startDefaultDate, endTime: endDefaultDate } = sessionState;

  return (
    !comparedMinutesAreSame(startCustomDate, startDefaultDate) ||
    !comparedMinutesAreSame(endCustomDate, endDefaultDate)
  );
};

export const cleanCustomSessionTimeArray = (sessionState) => {
  const customClone = _.cloneDeep(sessionState.customSessionTime);
  for (let key in customClone) {
    const val = customClone[key];

    if (!customSessionTimeDiffers(sessionState, val)) {
      delete customClone[key];
    }
  }
  return customClone;
};

const getDaysInMonth = (month, year) => {
  return new Date(year, month, 0).getDate();
};

export const createCalendarObj = (currentDate, format) => {
  let startDayTmp = moment(currentDate, format || "DD/MM/YYYY");

  const year = startDayTmp.year();
  const month = startDayTmp.month() + 1;
  const daysInMonth = getDaysInMonth(month, year);

  let monthObj = {};
  for (let i = 1; i <= daysInMonth; i++) {
    monthObj[i] = [];
  }
  return monthObj;
};

// export const getSessionFreeSlots = (patients, session, selectedDate) => {
//   const sessDuration = getSessionDurationInMinutes(session);
//   const patientsTodayAssignedToSession = _.filter(patients, (pt) => {
//     let assigned = false;
//     const bedasObj = getReviewData(pt);
//
//     if (
//       moment(bedasObj.date).isSame(selectedDate, "day") &&
//       bedasObj?.sessionId === session?.sessionId
//     ) {
//       assigned = true;
//     }
//
//     return assigned;
//   });
//
//   let slots = Math.floor(sessDuration / 30);
//
//   if (!_.isEmpty(patientsTodayAssignedToSession)) {
//     slots -= patientsTodayAssignedToSession.length * 30;
//   }
//
//   return slots;
// };

export const getMonthsBetweenTwoDates = (startDate, endDate) => {
  const dates = [];
  const end = moment(endDate || startDate);
  let currentDate = moment(startDate);

  while (currentDate.isSameOrBefore(end, "month")) {
    dates.push(currentDate.format("MM/YYYY"));
    currentDate = currentDate.add(1, "month");
  }
  return dates;
};

export const getSessionName = (session) => session?.sessionName;

export const printSessionStartDate = (session, isSingle = false) => {
  return moment(isSingle ? session?.from : session?.startDate).format(
    "DD/MM/YYYY",
  );
};

export const printSessionEndDate = (session, isSingle = false) => {
  return moment(isSingle ? session?.to : session?.endDate).format("DD/MM/YYYY");
};

export const getSessionStartHour = (
  sessionActive,
  session,
  dayOfWeek,
  isSingle = false,
) => {
  if (!sessionActive) return "-";

  let startDate;

  if (isSingle) {
    startDate = session?.from || session?.startDate;
  } else if (
    session?.customSessionTime &&
    session?.customSessionTime?.[dayOfWeek]
  ) {
    startDate = moment(
      session?.customSessionTime?.[dayOfWeek]?.startTime,
      "HH:mm",
    );
  } else {
    if (session?.slotStartTime) {
      const slotStartTime = moment.utc(session.slotStartTime, "HH:mm").local();

      startDate = moment(session.startDate).local().set({
        minute: slotStartTime.minute(),
        hour: slotStartTime.hour(),
      });
    } else {
      startDate = session?.startDate;
    }
  }

  return moment(startDate);
};

export const getSessionEndHour = (
  sessionActive,
  session,
  dayOfWeek,
  isSingle = false,
) => {
  if (!sessionActive) {
    return "-";
  }

  let endDate;

  if (isSingle) {
    endDate = session?.to || session?.endDate;
  } else if (
    session?.customSessionTime &&
    session?.customSessionTime?.[dayOfWeek]
  ) {
    // endDate = moment.utc(session?.customSessionTime?.[dayOfWeek]?.endTime, "HH:mm");
    endDate = moment(session?.customSessionTime?.[dayOfWeek]?.endTime, "HH:mm");
  } else {
    endDate = session?.endDate;
  }

  return moment(endDate);
};

export const getStartDate = (event, isReview, date, isSingle, isBars) =>
  isBars
    ? moment(event?.start).toISOString()
    : isReview
      ? getReviewData(event)?.date
      : getSessionStartHour(
          true,
          event,
          moment(date, "DD/MM/YYYY").isoWeekday(),
          isSingle,
        ) ||
        event?.startDate ||
        moment(event?.start || event?.from || event?.date).toISOString();

export const getEndDate = (event, isReview, date, isSingle, isBars) =>
  isBars
    ? moment(event?.end).toISOString()
    : isReview
      ? moment(getReviewData(event)?.date).add(30, "minutes").toISOString()
      : getSessionEndHour(
          true,
          event,
          moment(date, "DD/MM/YYYY").isoWeekday(),
          isSingle,
        ) ||
        event?.endDate ||
        moment(event?.end || event?.to).toISOString();

export const calculateDuration = (start, end, isSameDay, event) => {
  const endDate = !isSameDay
    ? moment(end)
    : moment(start).set({
        hour: moment(end).hours(),
        minute: moment(end).minutes(),
      });
  return moment.duration(moment(endDate).diff(moment(start))).asMinutes();
};

export const getSessionDurationInMinutes = (session, customHoursObj) => {
  const start = moment(
    customHoursObj && customHoursObj?.startTime
      ? customHoursObj?.startTime
      : session?.startDate,
  ).toISOString();
  const end = moment(
    customHoursObj && customHoursObj?.endTime
      ? customHoursObj?.endTime
      : session?.endDate,
  ).toISOString();

  const startDate = moment().set({
    hour: moment(start).hours(),
    minute: moment(start).minutes(),
  });
  const endDate = moment().set({
    hour: moment(end).hours(),
    minute: moment(end).minutes(),
  });

  return calculateDuration(startDate, endDate, true);
};

export const getSessionDurationString = (
  session,
  sessionActive,
  customHoursObject,
) => {
  if (!sessionActive) {
    return "-";
  }

  let str = "";
  const duration = getSessionDurationInMinutes(session, customHoursObject);
  let hours = Math.floor(duration / 60);
  let minutes = duration % 60;

  if (hours > 0) {
    str = `${hours}h`;
  }

  if (minutes > 0) {
    str = `${str} ${minutes}m`;
  }

  return str;
};

const groupEv = (columns, events, calendarDate) => {
  events.forEach((ev, ivIdx) => {
    let columnIndex = -1;
    const parentSession = ev?.parentSession;

    // const mainSessionStartHour = parseDate(
    //   getSessionStartHour(
    //     isSessionActive(ev),
    //     // !!ev.active,
    //     ev,
    //     moment(calendarDate, "DD/MM/YYYY").isoWeekday(),
    //     ev?.singleEvent,
    //   ),
    //   calendarDate,
    // );

    // const mainSessionEndHour = parseDate(
    //   getSessionEndHour(
    //     isSessionActive(ev),
    //     ev,
    //     moment(calendarDate, "DD/MM/YYYY").isoWeekday(),
    //     ev?.singleEvent,
    //   ),
    //   calendarDate,
    //   null,
    // );

    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];

      if (parentSession) {
        const overlappingEvent = column.find(
          (existingEvent) => existingEvent.sessionId === parentSession,
        );

        if (overlappingEvent) {
          columnIndex = i;
          break;
        }
      } else {
        const overlappingEvent = true;
        // const overlappingEvent = column.find((existingEvent) => {
        //   const existingEventStartHour = parseDate(
        //     getSessionStartHour(
        //       isSessionActive(existingEvent),
        //       existingEvent,
        //       moment(calendarDate, "DD/MM/YYYY").isoWeekday(),
        //       existingEvent?.singleEvent,
        //     ),
        //     calendarDate,
        //   );
        //
        //   const existingEventEndHour = parseDate(
        //     getSessionEndHour(
        //       isSessionActive(existingEvent),
        //       existingEvent,
        //       moment(calendarDate, "DD/MM/YYYY").isoWeekday(),
        //       existingEvent?.singleEvent,
        //     ),
        //     calendarDate,
        //   );
        //
        //   return (
        //     moment(mainSessionStartHour).isBetween(
        //       existingEventStartHour,
        //       existingEventEndHour,
        //       "minute",
        //       "()",
        //     ) ||
        //     mainSessionEndHour.isBetween(
        //       existingEventStartHour,
        //       existingEventEndHour,
        //       "minute",
        //       "()",
        //     )
        //   );
        // });

        if (!overlappingEvent) {
          columnIndex = i;
          break;
        }
      }
    }

    if (columnIndex === -1) {
      columnIndex = columns.length;
      columns.push([]);
    }

    columns[columnIndex].push(ev);
  });
};

export function getGroupedEvents(events, calendarDate) {
  const columns = [];

  if (!_.isEmpty(events)) {
    const assignedEvents = _.orderBy(
      _.filter(
        events,
        (ev) =>
          !!ev?.parentSession &&
          events.findIndex((evt) => evt?.sessionId === ev?.parentSession) >= 0,
      ),
      [(ev) => (ev?.parentSession ? 1 : 0)],
      ["asc"],
    );
    const singleEvents = _.orderBy(
      _.filter(events, (ev) => !!ev?.singleEvent && !ev?.parentSession),
      [(ev) => ev?.locked],
      "desc",
    );
    const regularEvents = _.orderBy(
      _.filter(events, (ev) => !ev?.singleEvent && !ev?.parentSession),
      [(ev) => cleanDate(ev?.startDate).format("HH:mm")],
      "asc",
    );

    groupEv(columns, regularEvents, calendarDate);
    groupEv(columns, singleEvents, calendarDate);
    groupEv(columns, assignedEvents, calendarDate);
  }

  let groupedEvents = [];
  let nonamedSessions = 0;

  for (let eventsArray of columns) {
    if (!eventsArray?.[0]?.sessionName) ++nonamedSessions;

    const title = eventsArray?.[0]?.sessionName ? eventsArray?.[0]?.sessionName : `${"Appointment Session"} ${nonamedSessions}`;

    groupedEvents.push({
      // colId: uuidv4(),
      colId: eventsArray?.[0]?.sessionId || uuidv4(),
      events: eventsArray,
      title,
    });
  }

  return groupedEvents;
}

export const getLockedSlots = (events, onlySingle = false) => {
  const predicate = (ev) =>
    (onlySingle ? !!ev?.singleEvent : true) && !!ev?.locked;

  return _.filter(events, (ev) => {
    return predicate(ev);
  });
};

export const sortRecords = ({
  customDirections = {},
  customPaths = {},
  records = [],
  fields = [],
}) => {
  if (_.isEmpty(fields)) return records;

  let sorted = _.cloneDeep(records);

  const sortIteratees = [];
  const directionIteratees = [];

  fields.forEach(({ name, direction }) => {
    const field = name;
    const fieldDirection = customDirections?.[name] || direction || "asc";

    if (Array.isArray(customPaths[name])) {
      customPaths[name].forEach((path) => {
        sortIteratees.push(path);
        directionIteratees.push(fieldDirection);
      });
    } else if (customPaths[name]) {
      sortIteratees.push(customPaths[name]);
      directionIteratees.push(fieldDirection);
    } else if (Array.isArray(field)) {
      field.forEach((f) => {
        sortIteratees.push(f);
        directionIteratees.push(direction);
      });
    } else if (_.isObject(field)) {
      sortIteratees.push((record) =>
        _.values(_.pick(record, Object.values(field))).join(" "),
      );
    } else {
      sortIteratees.push((record) => record[field]);
    }

    if (customDirections && customDirections[name]) {
      sortIteratees[sortIteratees.length - 1] = (record) =>
        customDirections[name](record, direction);
    }
  });

  return _.orderBy(sorted, sortIteratees, directionIteratees);
};

export const getReviewsScheduledOnGivenDate = (
  allReviews,
  predicate,
  customGranularity = "",
) => {
  return _.filter(allReviews, (review) =>
    _.isFunction(predicate)
      ? predicate(review)
      : moment(getReviewData(review)?.date).isSame(
          predicate,
          customGranularity || "date",
        ),
  );
};

export const getPatientsReviewStatus = (patient) => {
  if (!patient) {
    return ED_REVIEW_PATIENT_STATUSES.UNREGISTERED;
  }

  const reviewObject = getReviewData(patient);
  const isCancelled =
    reviewObject?.cancelled ||
    reviewObject?.status === "cancelled" ||
    reviewObject?.status === "canceled";

  return isCancelled
    ? ED_REVIEW_PATIENT_STATUSES.CANCELLED
    : !reviewObject?.date
      ? ED_REVIEW_PATIENT_STATUSES.NOT_ASSIGNED
      : reviewObject?.status === "started"
        ? ED_REVIEW_PATIENT_STATUSES.REGISTERED
        : reviewObject?.status === "inReview"
          ? ED_REVIEW_PATIENT_STATUSES.IN_REVIEW
          : null;
};

export const parseDate = (momentObj, date, dateFormat = "DD/MM/YYYY") =>
  moment(date, dateFormat).set({
    hours: moment(momentObj).hours(),
    minutes: moment(momentObj).minutes(),
    seconds: moment(momentObj).seconds(),
  });

const getAvailableLockColumnOptionsVersionOneTineForEachSlot = (
  columns,
  patientsAssigned,
  calendarDate,
) => {
  let res = [];
  const columnsClone = _.cloneDeep(columns);

  const calculateInternalDifference = (startTime, endTime) => {
    const roundedStart = moment(startTime).startOf("minute");
    const roundedEnd = moment(endTime).startOf("minute");

    return moment(roundedEnd).diff(moment(roundedStart), "minutes");
  };

  if (!_isEmpty(columnsClone)) {
    columnsClone.forEach((sessionColumn) => {
      const evHrs = [];
      const resultArray = [];
      const mainSessions = sessionColumn.events.filter(
        (session) => !session?.parentSession && !session?.singleEvent,
      );
      const assignedSessions = sessionColumn.events.filter(
        (session) => !!session?.parentSession || session?.singleEvent,
      );

      // console.log("assignedSessions:", sessionColumn.events, assignedSessions)
      for (let ev of mainSessions) {
        if (!!ev?.active || !!ev?.locked || !!isSessionActive(ev)) {
          console.log("sessionId:", ev.sessionId)
          const slotDuration = getSessionSlotDuration(ev);
          const startDate = getStartDate(ev, false, calendarDate);
          const endDate = getEndDate(ev, false, calendarDate);
          const assignedEvents = _.filter(assignedSessions, {
            parentSession: ev?.sessionId,
          }).sort((a) => a?.startDate);

          const patientsAssignedToSession = _.filter(
            patientsAssigned,
            (patient) => {
              return getReviewData(patient)?.assignedSession === ev.sessionId;
            },
          );

          console.log("PATIENTS", patientsAssignedToSession)


          _.orderBy(
            patientsAssignedToSession,
            (patient) => getReviewData(patient)?.date,
            "asc",
          ).forEach((pt) => {
            assignedEvents.push({
              startDate: moment(getReviewData(pt)?.date),
              endDate: moment(getReviewData(pt)?.date).add(
                slotDuration,
                "minutes",
              ),
              singleEvent: true,
              locked: false,
              assignedSession: ev.sessionId,
            });
          });

          evHrs.push({
            startDate: parseDate(startDate, calendarDate),
            endDate: parseDate(endDate, calendarDate),
            slotDuration,
            assignedEvents,
            locked: ev?.locked,
            sessionId: ev.sessionId,
          });
        }
      }

      // Generowanie wolnych slotów na podstawie evHrs
      evHrs.forEach((ev) => {
        let { startDate, endDate, slotDuration, assignedEvents } = ev;

        // Jeśli brak przypisanych wydarzeń, cały przedział między startDate a endDate jest dostępny
        if (assignedEvents.length === 0) {
          let totalSlotDuration = calculateInternalDifference(
            startDate,
            endDate,
          );
          if (totalSlotDuration >= slotDuration) {
            // Dodajemy sloty dla całego przedziału
            let currentStart = moment(startDate);
            while (currentStart.isBefore(endDate)) {
              let currentEnd = moment(currentStart).add(slotDuration, "minutes");
              if (currentEnd.isSameOrBefore(endDate, 'minute')) {
                resultArray.push({
                  id: uuidv4(),
                  locked: ev?.locked,
                  sessionId: ev.sessionId,
                  slotDuration: ev.slotDuration,
                  startDate: moment(currentStart.toISOString()),
                  endDate: moment(currentEnd.toISOString()),
                });
              }
              currentStart = currentEnd;
            }
          }
        } else {
          // Sprawdź sloty między istniejącymi wydarzeniami
          let previousEnd = moment(startDate);

          assignedEvents.forEach((assignedEvent, index) => {
            const assignedStart = parseDate(
              assignedEvent.startDate,
              calendarDate,
            );
            const assignedEnd = parseDate(assignedEvent.endDate, calendarDate);

            // Sprawdź, czy jest wystarczająca przerwa między końcem poprzedniego wydarzenia a początkiem bieżącego
            if (previousEnd.isSameOrBefore(assignedStart, 'minute')) {
              let gapDuration = calculateInternalDifference(
                previousEnd,
                assignedStart,
              );

              if (gapDuration >= slotDuration) {
                // Dodaj sloty w tej przerwie
                let gapStart = previousEnd;
                while (gapStart.isSameOrBefore(assignedStart, 'minute')) {
                  let gapEnd = moment(gapStart).add(slotDuration, "minutes");
                  if (gapEnd.isSameOrBefore(assignedStart, 'minute')) {
                    resultArray.push({
                      sessionId: ev.sessionId,
                      locked: ev?.locked,
                      startDate: moment(gapStart.toISOString()),
                      slotDuration: ev.slotDuration,
                      endDate: moment(gapEnd.toISOString()),
                    });
                  }
                  gapStart = gapEnd;
                }
              }
            }

            // Ustaw koniec poprzedniego wydarzenia na bieżące zakończenie
            previousEnd = moment(assignedEnd);
          });

          // Sprawdź ostatni slot od końca ostatniego wydarzenia do końca dnia
          if (previousEnd.isSameOrBefore(endDate, 'minute')) {
            let finalGapDuration = calculateInternalDifference(
              previousEnd,
              endDate,
            );

            if (finalGapDuration >= slotDuration) {
              // Dodaj sloty w tej przerwie
              let gapStart = previousEnd;
              while (gapStart.isSameOrBefore(endDate, 'minute')) {
                let gapEnd = moment(gapStart).add(slotDuration, "minutes");
                if (gapEnd.isSameOrBefore(endDate, 'minute')) {
                  resultArray.push({
                    sessionId: ev.sessionId,
                    locked: ev?.locked,
                    startDate: moment(gapStart.toISOString()),
                    slotDuration: ev.slotDuration,
                    endDate: moment(gapEnd.toISOString()),
                  });
                }
                gapStart = gapEnd;
              }
            }
          }
        }
      });

      sessionColumn.events = resultArray;
      res.push(sessionColumn);
    });
  }

  console.log("RES:", res);
  return res;
};

const getAvailableLockColumnOptions = (
  columns,
  patientsAssigned,
  calendarDate,
) => {
  let res = [];
  const columnsClone = _.cloneDeep(columns);

  const calculateInternalDifference = (startTime, endTime) => {
    const roundedStart = moment(startTime).startOf("minute");
    const roundedEnd = moment(endTime).startOf("minute");

    return moment(roundedEnd).diff(moment(roundedStart), "minutes");
  };

  if (!_isEmpty(columnsClone)) {
    columnsClone.forEach((sessionColumn) => {
      const evHrs = [];
      const resultArray = [];
      const mainSessions = sessionColumn.events.filter(
        (session) => !session?.parentSession && !session?.singleEvent,
      );
      const assignedSessions = sessionColumn.events.filter(
        (session) => !!session?.parentSession || session?.singleEvent,
      );

      for (let ev of mainSessions) {
        if (!!ev?.active || !!ev?.locked || !!isSessionActive(ev)) {
          const slotDuration = getSessionSlotDuration(ev);
          const startDate = getStartDate(ev, false, calendarDate);
          const endDate = getEndDate(ev, false, calendarDate);
          const assignedEvents = _.filter(assignedSessions, {
            parentSession: ev?.sessionId,
          }).sort((a) => a?.startDate);
          const patientsAssignedToSession = _.filter(
            patientsAssigned,
            (patient) => {
              return getReviewData(patient)?.assignedSession === ev.sessionId;
            },
          );

          _.orderBy(
            patientsAssignedToSession,
            (patient) => getReviewData(patient)?.date,
            "asc",
          ).forEach((pt) => {
            assignedEvents.push({
              startDate: moment(getReviewData(pt)?.date),
              endDate: moment(getReviewData(pt)?.date).add(
                slotDuration,
                "minutes",
              ),
              singleEvent: true,
              locked: false,
              assignedSession: ev.sessionId,
            });
          });

          evHrs.push({
            startDate: parseDate(startDate, calendarDate),
            endDate: parseDate(endDate, calendarDate),
            slotDuration,
            assignedEvents,
            locked: ev?.locked,
            sessionId: ev.sessionId,
          });
        }
      }

      // Generowanie wolnych slotów na podstawie evHrs
      evHrs.forEach((ev) => {
        let { startDate, endDate, slotDuration, assignedEvents } = ev;

        // Jeśli brak przypisanych wydarzeń, cały przedział między startDate a endDate jest dostępny
        if (assignedEvents.length === 0) {
          let totalSlotDuration = calculateInternalDifference(
            startDate,
            endDate,
          );
          if (totalSlotDuration >= slotDuration) {
            resultArray.push({
              id: uuidv4(),
              locked: ev?.locked,
              sessionId: ev.sessionId,
              slotDuration: ev.slotDuration,
              startDate: moment(startDate.toISOString()),
              endDate: moment(endDate.toISOString()),
            });
          }
        } else {
          // Sprawdź sloty między istniejącymi wydarzeniami
          let previousEnd = moment(startDate);

          assignedEvents.forEach((assignedEvent, index) => {
            const assignedStart = parseDate(
              assignedEvent.startDate,
              calendarDate,
            );
            const assignedEnd = parseDate(assignedEvent.endDate, calendarDate);

            // Sprawdź, czy jest wystarczająca przerwa między końcem poprzedniego wydarzenia a początkiem bieżącego
            if (previousEnd.isBefore(assignedStart)) {
              let gapDuration = calculateInternalDifference(
                previousEnd,
                assignedStart,
              );

              if (gapDuration >= slotDuration) {
                resultArray.push({
                  sessionId: ev.sessionId,
                  locked: ev?.locked,
                  startDate: moment(previousEnd.toISOString()),
                  slotDuration: ev.slotDuration,
                  endDate: moment(assignedStart.toISOString()),
                });
              }
            }

            // Ustaw koniec poprzedniego wydarzenia na bieżące zakończenie
            previousEnd = moment(assignedEnd);
          });

          // Sprawdź ostatni slot od końca ostatniego wydarzenia do końca dnia
          if (previousEnd.isBefore(endDate)) {
            let finalGapDuration = calculateInternalDifference(
              previousEnd,
              endDate,
            );

            if (finalGapDuration >= slotDuration) {
              resultArray.push({
                sessionId: ev.sessionId,
                locked: ev?.locked,
                startDate: moment(previousEnd.toISOString()),
                slotDuration: ev.slotDuration,
                endDate: moment(endDate.toISOString()),
              });
            }
          }
        }
      });

      sessionColumn.events = resultArray;
      res.push(sessionColumn);
    });
  }

  return res;
};

const getNonAssignedColumnOptions = (columns, calendarDate) => {
  const columnsClone = _.cloneDeep(columns);
  let res = [];

  const parseDate = (first, second) =>
    moment(first).set({
      hours: moment(first).hours(),
      minutes: moment(first).minutes(),
      date: moment(second, "DD/MM/YYYY").date(),
      month: moment(second, "DD/MM/YYYY").month(),
      year: moment(second, "DD/MM/YYYY").year(),
    });

  if (!_isEmpty(columnsClone)) {
    columnsClone.forEach((sessionColumn, columnIndex) => {
      let evHrs = [];
      let currEvHrsIndex = 0;
      const resultArray = [];
      const currentEvents = sessionColumn.events;

      const startOfDay = moment(calendarDate, "DD/MM/YYYY").startOf("day");
      const endOfDay = moment(calendarDate, "DD/MM/YYYY").endOf("day");
      let currentTime = moment(moment(startOfDay).toISOString());

      for (let ev of currentEvents) {
        const startDate = moment(
          getStartDate(ev, false, calendarDate, ev?.singleEvent),
        ).set("year", moment().year());
        const endDate = moment(
          getEndDate(ev, false, calendarDate, ev?.singleEvent),
        ).set("year", moment().year());

        evHrs.push({
          startDate: parseDate(startDate, calendarDate),
          endDate: parseDate(endDate, calendarDate),
          sessionId: "outsideSession",
        });
      }

      evHrs = evHrs.sort((ev) => moment(ev?.startDate).toISOString());

      while (currentTime.isBefore(endOfDay) && currEvHrsIndex <= evHrs.length) {
        if (currentTime.isBefore(moment(evHrs[currEvHrsIndex]?.startDate))) {
          resultArray.push({
            ...evHrs[currEvHrsIndex],
            sessionId: "outsideSession",
            startDate: moment(currentTime),
            endDate: moment(evHrs[currEvHrsIndex]?.startDate),
          });

          currentTime = moment(evHrs[currEvHrsIndex]?.startDate).add(
            1,
            "minute",
          );
        }

        if (
          currentTime.isBetween(
            moment(evHrs[currEvHrsIndex]?.startDate),
            moment(evHrs[currEvHrsIndex]?.endDate),
            "minute",
            "[]",
          )
        ) {
          currentTime = moment(evHrs[currEvHrsIndex]?.endDate).set({
            hours: moment(evHrs[currEvHrsIndex]?.endDate).hours(),
            minutes: moment(evHrs[currEvHrsIndex]?.endDate).minutes(),
            date: moment(calendarDate, "DD/MM/YYYY").date(),
            month: moment(calendarDate, "DD/MM/YYYY").month(),
            year: moment(calendarDate, "DD/MM/YYYY").year(),
            // year: moment().year(),
          });
        }

        if (currentTime.isAfter(moment(evHrs[currEvHrsIndex]?.endDate))) {
          resultArray.push({
            sessionId: "outsideSession",
            startDate: moment(evHrs[currEvHrsIndex - 1]?.endDate),
            endDate: moment(endOfDay.subtract(1, "minute")),
          });
        }

        currEvHrsIndex++;
      }

      sessionColumn.events = resultArray;
      res.push(sessionColumn);
    });

    return res;
  }
};

export const getOptionAvailableOptions = (
  option = SLOT_MANIPULATION_OPTIONS.LOCK_SLOT,
  eventColumns,
  calendarDate,
  dailyPatientReviews,
) => {
  if (!_.isObject(eventColumns)) {
    return null;
  } else if (
    option === SLOT_MANIPULATION_OPTIONS.ASSIGN ||
    option === SLOT_MANIPULATION_OPTIONS.LOCK_SLOT
  ) {
    return getAvailableLockColumnOptionsVersionOneTineForEachSlot(
      eventColumns,
      dailyPatientReviews,
      calendarDate,
    );
  } else if (option === SLOT_MANIPULATION_OPTIONS.CREATE_SLOT) {
    return getNonAssignedColumnOptions(eventColumns, calendarDate);
  } else {
    return null;
  }
};

export const getWarningColor = (slotsObject) => {
  if (!_.isObject(slotsObject)) return "";

  let freeSlots = 0;
  let totalSlots = 0;

  for (let sessionId in slotsObject) {
    const sessionIdSchedule = slotsObject[sessionId];

    for (let slot in sessionIdSchedule) {
      totalSlots++;

      if (_.isNil(sessionIdSchedule[slot])) {
        freeSlots++;
      }
    }
  }

  if (freeSlots === 0 && freeSlots === totalSlots) {
    return "";
  } else {
    if (freeSlots === totalSlots) return "clear";

    return freeSlots < 3 ? "alert" : freeSlots < 6 ? "warning" : "clear";
  }

  return "";
};

export const getTimerFontColor = (countdownTo) => {
  const difference = -calculateDifferenceInMinutes(countdownTo);

  if (difference > TIMER_COUNTDOWN_MINUTES.yellow) {
    return "green";
  } else if (
    difference < TIMER_COUNTDOWN_MINUTES.yellow &&
    difference > TIMER_COUNTDOWN_MINUTES.red
  ) {
    return "yellow";
  } else {
    return "red";
  }
};

////////////////////////////////////////////////
////   TO API SEND PREP   ////
///////////////////////////////////////////////

const prepareEdReviewDataToDispatch = (field, value, originalValue) => {
  let val = null;

  if (value === originalValue) {
    return val;
  }

  switch (field) {
    case "status":
      const possibleValues = [
        "started",
        "registered",
        "cancelled",
        "reviewed",
        "inReview",
      ];
      if (possibleValues.includes(value)) {
        val = value;
      }
      break;
    case "assignedSession":
      if (isValidUUID(value)) {
        val = value;
      }
      break;
    case "date":
      if (moment(value).isValid()) {
        val = value;
      } else {
        val = moment(value);
      }
      break;
    case "reason":
      if (_.isString(value)) {
        val = value;
      }
      break;
    case "cancelled":
    case "barsAppointment":
      if (_.isBoolean(value)) {
        val = value;
      }
      break;
    default:
      return val;
  }

  return val;
};

export const getPreparedReviewObject = ({
  modifiedValues,
  originalValues,
  objectKey,
}) => {
  if (!modifiedValues) {
    return [[{ bedas: undefined }], []];
  }

  const modifiedValuesArr = [];
  const datesArr = [];

  const valuesMap = {
    status: prepareEdReviewDataToDispatch(
      "status",
      modifiedValues?.status,
      originalValues?.status,
    ),
    assignedSession: prepareEdReviewDataToDispatch(
      "assignedSession",
      modifiedValues?.assignedSession,
      originalValues?.assignedSession,
    ),
    date: prepareEdReviewDataToDispatch(
      "date",
      modifiedValues?.date,
      originalValues?.date,
    ),
    reason: prepareEdReviewDataToDispatch(
      "reason",
      modifiedValues?.reason,
      originalValues?.reason,
    ),
    cancelled: prepareEdReviewDataToDispatch(
      "cancelled",
      modifiedValues?.isCancelled,
      originalValues?.cancelled,
    ),
    barsAppointment: prepareEdReviewDataToDispatch(
      "barsAppointment",
      modifiedValues?.barsAppointment,
      originalValues?.barsAppointment,
    ),
  };

  for (let key in valuesMap) {
    if (!_.isNil(valuesMap[key])) {
      key === "date"
        ? datesArr.push({ [`${objectKey}.${key}`]: valuesMap[key] })
        : modifiedValuesArr.push({ [`${objectKey}.${key}`]: valuesMap[key] });
    }
  }

  return [modifiedValuesArr, datesArr];
};

export const filterBarsReviews = (
  reviews,
  { day, cancelled = false, sessionId = "" },
) => {
  return _.filter(reviews, (rev) => {
    let sessionIdValid = true;

    const isDateValid =
      moment(rev?.start).isSame(moment(day, "DD/MM/YYYY"), "day") ||
      moment(rev?.end).isSame(moment(day, "DD/MM/YYYY"), "day");

    if (sessionId) sessionIdValid = rev?.assignedSession === sessionId;

    return sessionIdValid && isDateValid;
  });
};

export const filterReviewsByDayAndSessionId = (
  reviews,
  { day, cancelled = false, sessionId = "", bars = false },
) => {
  const reviewsClone = _.cloneDeep(reviews);

  if (bars)
    return filterBarsReviews(reviewsClone, { day, cancelled, sessionId });

  return _.filter(reviewsClone, (rev) => {
    let cancelValid = true;
    let sessionIdValid = true;

    const isDateValid = moment(rev?.bedas?.date).isSame(
      moment(day, "DD/MM/YYYY"),
      "day",
    );

    if (sessionId) {
      sessionIdValid = getReviewData(rev)?.assignedSession === sessionId;
    }

    if (cancelled) {
      cancelValid = getReviewData(rev)?.cancelled === cancelled;
    }

    return cancelValid && sessionIdValid && isDateValid;
  });
};

export const hydrateSessionSlotsWithAssignedPatients = (
  reviews,
  slotObj,
  sessionData,
  daySlots,
  activeContext,
) => {
  let toReturn = {};
  const slotsClone = _.cloneDeep(slotObj);
  const reviewsClone = _.cloneDeep(reviews);
  const isBars = activeContext === KIND_OF_REVIEW_STATE.BARS;

  for (let hour in slotsClone) {
    const pt = _.find(
      reviewsClone,
      (rev) =>
        moment(isBars ? rev?.start : rev?.bedas?.date).format("HH:mm") === hour,
    );
    let objectValue =
      hour === "slotDuration"
        ? sessionData?.slotDurationInMinutes
        : sessionData?.locked
          ? "locked"
          : pt?.patient?.hospitalNumber || null;

    if (isBars && pt) {
      //  FIND NHS NUMBER IN PARSED PATIENT!!!
      _.find(reviewsClone, (review) => {
        if (review.patient && review.patient.identifier) {
          const nhsObj = _.find(review.patient.identifier, (id) =>
            id.system.includes("nhs-number"),
          );

          if (nhsObj) objectValue = nhsObj.value;
          return nhsObj.value;
        }
      });
    }

    toReturn[hour] = objectValue;

    if (
      sessionData?.singleEvent &&
      daySlots?.[sessionData?.parentSession || sessionData?.assignedSession] &&
      !daySlots[sessionData?.parentSession || sessionData?.assignedSession]?.[
        hour
      ]
    ) {
      daySlots[sessionData.parentSession || sessionData?.assignedSession][
        hour
      ] = "locked";
    }
  }

  return toReturn;
};

////////////////////////////////////////////////
//// EVENT POSITIONING  ////
///////////////////////////////////////////////

export const calculateEventHeight = ({
  event,
  eventSlotHeight,
  offset,
  eventDate,
  isReview,
  isBars,
  parentSession,
}) => {
  const durationInMinutes =
    parentSession || event?.singleEvent
      ? getSessionSlotDuration(parentSession || event)
      : calculateDuration(
          getStartDate(event, isReview, eventDate, false, isBars),
          getEndDate(event, isReview, eventDate, false, isBars),
          true,
        );

  let height = (durationInMinutes / 30) * eventSlotHeight - 2 * offset;

  if (isReview) {
    height -= 1 * OFFSET;
  }

  return `${height}px`;
};

export const calculateEventTop = ({
  event,
  slotHeight,
  offset,
  isReview,
  eventDate,
  isBars,
}) => {
  const startDate = getStartDate(event, isReview, eventDate, false, isBars);
  const startOfDay = moment(startDate).startOf("day");
  const minutesFromStartOfDay = moment(startDate).diff(startOfDay, "minutes");
  let top = (minutesFromStartOfDay / 30) * slotHeight + offset;

  if (isReview) {
    top += 1 * OFFSET;
  }

  return `${top}px`;
};

export const calculateEventLeft = ({
  columnIndex,
  eventSlotWidth,
  isReview,
}) => {
  if (!columnIndex) {
    return 0;
  }

  const colIdx = columnIndex <= 0 ? 0 : columnIndex;
  let left = DEFAULT_LEFT + colIdx * (eventSlotWidth - OFFSET);
  if (colIdx > 0) {
    left += COLUMN_GAP * colIdx;
  }

  if (isReview) {
    left += 2 * OFFSET;
  }
  return `${left + 5}px`;
};

export const getVisibleWidth = (element) => {
  if (!element) return 0;

  const elementRect = element.getBoundingClientRect();
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  const left = Math.max(elementRect.left, 0);
  const right = Math.min(elementRect.right, windowWidth);

  return Math.max(right - left, 0);
};
